﻿import { IPatientSlimDto } from '@/shared/interfaces/consultation/IPatientSlimDto';
import { IPetSlimDto } from '@/shared/interfaces/consultation/IPetSlimDto';

import {
    AvailableRelationTypeEnum,
    CaregiverRoleEnum,
    CaregiverSpecialization,
    ConsultationTypeEnum,
    DeviceTypeEnum,
    HouseholdMemberType,
    InsurancePolicyProviderType,
    OpenVideoConsultationStatusEnum,
    PartnerStatusEnum,
    PaymentProvider,
    PetSexEnum,
    PetTypeEnum,
    PhysioConsultationOutcomeEnum,
    PlanInterval,
    ServiceTypeEnum,
    SignupDoctorResponseEnum,
    SubscriptionStatusEnum,
    UserTypeEnum,
    VetConsultationOutcomeEnum,
    VetConsultationReasonEnum,
} from './enums';
import { AppEdition } from './appEdition';

export interface ResponseError extends Error {
    status?: number;
}

export interface IStatus<T> {
    readonly Result?: T | null;
    readonly Errors: IStatusError[];
    readonly HasErrors: boolean;
}

export interface IStatusError {
    readonly Key: string;
    readonly Value: string;
}

export interface IBootstrappedData {
    LeToken: string | null | undefined;
    UserId: number;
    CurrentLocale: string;
    AppEdition: AppEdition;
}

export interface IHash {
    [indexer: string]: string;
}

export interface ITimelineMessage {
    userUid: string;
    isDoctorType: boolean;
    message: string;
    sentOn: string;
    attachments: IAttachment[];
    avatarUrl?: string;
    caregiverName?: string;
}

export interface IAttachment {
    fileUid: string;
    fileName: string;
    canShowPreview: boolean;
}

export interface IVideoConferenceDtoResponse {
    isVideoCallPending: boolean;
}

// ReSharper disable InconsistentNaming
export interface IBaseDto {
    ID: number;
    UID: string;
}

export interface ISelfResponse {
    Me: IPatientDto;
    Partner: IAdultHouseholdMemberDto;
    Kids: IPatientDto[];
    AdolescentHouseholdMembers: IAdolescentHouseholdMemberDto[];
    AdultHouseholdMembers: IAdultHouseholdMemberDto[];
}

export interface IValidateCpr {
    CPR: string;
}

export interface IPatientDto {
    Email: string;
    CPR: string;
    DateOfBirth?: string | null;
    PolicyNumber: string;
    CprValidated: boolean;
    MobilePhone: string;
    Company: IBaseDto;
    User: IUserDto;
    AcceptMarketing: boolean;
    IsParentalAuthorityConfirmed?: boolean;
}

export interface IUserDto extends IBaseDto {
    FullName: string;
    UserType: UserTypeEnum;
    AvatarUrl?: string;
    PhoneNumber: string;
}

export interface IAdultHouseholdMemberDto extends IBaseDto {
    Name: string;
    Email: string;
    Cpr: string;
    Status: PartnerStatusEnum;
}

export interface IAdolescentHouseholdMemberDto {
    ID: number;
    UserUid: string | null;
    Name: string;
    Email: string;
    Cpr: string;
    Status: PartnerStatusEnum;
}

export interface SelectChildDto {
    Uid: string;
    FullName: string;
    ID: string;
}

export interface IKid {
    UID: string;
    Name: string;
    CPR: string;
    FormattedCpr?: string;
    IsParentalAuthorityConfirmed?: boolean;
}

export interface IChildParentalAuthorityStatus {
    IsConfirmed: boolean | null;
    Cpr: string;
}

export interface IPetDto {
    Id: string;
    Name: string;
    Type: PetTypeEnum;
    Sex: PetSexEnum;
    Race: string;
    Birthdate: Date;
    WeightKg: number;
}

export interface IPet extends IPetDto {
    IconPath: string;
}

export interface IAdultHouseholdMember extends IBaseDto {
    Name: string;
    Email: string;
    Status: PartnerStatusEnum;
    Cpr: string;
}

export interface IDeleteAdultHouseholdMemberRequest {
    Cpr: string;
}

export interface IAddKidRequest {
    Name: string;
    CPR: string;
    ShouldCheckParentalAuthority: boolean;
}

export interface IEditKidRequest extends IAddKidRequest {
    UID: string;
}

export interface IAddPetRequest {
    Name: string;
    Type: PetTypeEnum;
    Sex: PetSexEnum;
    Race: string;
    Birthdate: Date;
    WeightKg?: number;
}

export interface ISetPolicyNumberRequest {
    Number: string;
    InsurancePolicyProviderType: InsurancePolicyProviderType;
}

export interface ISetPolicyNumberResponse {
    InsurancePolicyCompanyName: string;
}

export interface IDoctorDto {
    Email: string;
    PasswordHash: string;
    PasswordSalt: string;
    Title: string;
    User: IUserDto;
}

export interface IConsultationMessageDto extends IBaseDto {
    Consultation: IConsultationDto;
    Message: string;
    FromUser: IUserDto;
    Attachments: IConsultationMessageAttachmentDto[];
    SentOn?: string;
    PaymentVerificationData: IPaymentVerificationData;
    AiNurseInvolved?: boolean;
    AiNurseWasHelpful?: boolean | null;
}

export interface ICaregiverSpaceConsultationMessageDto extends IConsultationMessageDto {
    CaregiverRole?: CaregiverRoleEnum;
}

export interface ICancelFutureConsultationRequest {
    Reason: string;
}

export interface ICancelConsultationReasonDto {
    DefaultMessage: string;
}

export interface IPaymentVerificationData {
    Currency: string;
    PaidOn: Date;
    SalesPrice: number;
    TransactionId: string;
    PaymentProvider: PaymentProvider;
}

export interface IConsultationDto extends IBaseConsultationDto {
    Patient: IPatientDto;
    Doctor: IDoctorDto;
    OnBehalfOfPatient: IPatientDto;
    OnBehalfOfPet: IPetDto;
    Company: ICompanyDto;
    Messages: IConsultationMessageDto[];
    VideoMessages: IConsultationMessageDto[];
    ConsultationVideoSchedules: IConsultationVideoScheduleDto[];
    PaidOn: string;
    OpensOn: string;
    ClosesOn: string;
    RequiresPayment: boolean;
    PaymentVerificationData: IPaymentVerificationData;
    IsDeleted: boolean;
    NotifyOwnDoctor: boolean;
    VetConsultationOutcome?: VetConsultationOutcomeEnum;
    VetConsultationReason?: VetConsultationReasonEnum;
    PhysioConsultationOutcome?: PhysioConsultationOutcomeEnum;
    DiagnosisCode?: string | null;
    DiagnosisDescription?: string | null;
    IsParticularlyDemanding: boolean;
}

export interface IBaseConsultationDto extends IBaseDto {
    CreatedOn: string;
    Subject: string;
    ArchivedOn?: string;
    FinishedOn?: string;
    ScheduledOn?: string | null;
    ConsultationType: ConsultationTypeEnum;
    HasUnreadMessages: boolean;
    Rating: number;
    RatingComment: string;
    HasResponsesFromDoctor: boolean;
    CanRate: boolean;
    IsRated: boolean;
    Coupon?: ICouponDto | null;
    CouponRedemption?: ICouponRedemptionDto | null;
}

export interface ICouponDto extends IBaseDto {
    Code?: string | null;
    Name?: string | null;
    Description?: string | null;
    GeneralConsultationsAllowed?: number;
}

export interface ICouponRedemptionDto extends IBaseDto {
    NumberOfActivations: number;
    ExpiresOn: string;
    Coupon?: ICouponDto | null;
}

export interface IConsultationMessageAttachmentDto extends IBaseDto {
    ConsultationMessage: IConsultationMessageDto;
    File: IFileConsultationDto;
}

export interface IConsultationVideoAttachmentDto extends IBaseDto {
    Consultation: IConsultationDto;
    File: IFileConsultationDto;
}

export interface IFileConsultationDto extends IFileDto {
    ConsultationMessage: IConsultationMessageDto;
}

export interface IFileDto extends IBaseDto {
    FileName: string;
    FileByteSize: number;
    FileUrl: string;
}

export interface IConsultationVideoScheduleDto extends IBaseDto {
    Consultation: IConsultationDto;
    StartedOn?: Date;
    FinishedOn?: Date;
    ScheduledOn: Date;
    IsQueued: boolean;
    VideoCallsDto: any;
    Attachments: IConsultationVideoAttachmentDto[];
}

export interface IContinueMessageConsultationRequest {
    ExistingMessageGuid?: string;
    Message: string;
    FileUids: string[];
    VerifyPayment?: IPaymentVerificationRequest;
}

export interface IRateConsultationRequest {
    Rating: number;
    RatingComment: string;
}

export interface IGetScheduleRequest {
    FromDate: string;
    ToDate: string;
    ConsultationType: ConsultationTypeEnum;
    ConsultationDraftScheduledOn?: string;
}

export interface ITimeslotsByDay {
    day: string;
    timeslots: Date[];
    availableCaregivers: string[];
}

export interface ITimeSlotDto {
    Start: string;
    // This is a list of UUIDs
    AvailableCaregivers: Array<string>;
    AvailableSlots: number;
}

export interface ICompanyDropdownResponse {
    Key: number;
    Value: string;
}

export interface IQuestionAnswerDto {
    Question: string;
    Answer: string;
}

export interface ISubscriptionDto {
    SubscriptionId?: string | null;
    SubscriptionPlanId?: string | null;
    Status: SubscriptionStatusEnum;
    CurrentPeriodStart?: Date | null;
    CurrentPeriodEnd?: Date | null;
    Start: Date;
    End?: Date | null;
    CanceledOn?: Date | null;
    TrialEnds?: Date | null;
    Patient?: IPatientDto | null;
    IssuedToPatient?: IPatientDto | null;
    PaymentInfoLink?: string | null;
}

export interface ISubscriptionPlan {
    Id?: string | null;
    Name?: string | null;
    Amount: number;
    Currency?: string | null;
    Interval: PlanInterval;
    IntervalCount: number;
    TrialPeriodDays?: number | null;
    IsHidden: boolean;
    RequiredReferralCode?: string | null;
    ConsultationType?: ConsultationTypeEnum | null;
    FixedCount: number;
}

export interface IOpenVideoConsultationRequest extends IBaseOpenConsultationRequest {
    ScheduledOn?: Date;
    PreferredCaregiverUid?: string | null;
    ConsultationUid?: string | null;
    FileUids: string[];
}

export interface IRescheduleVideoConsultationRequest {
    ConsultationType: ConsultationTypeEnum;
    ScheduledOn?: Date;
    PreferredCaregiverUid?: string | null;
    UID?: string | null;
}

export interface IRescheduleConsultationAsAdminOrCaregiverLeadRequest {
    ConsultationUid: string;
    RescheduledDateTime?: Date;
    PreferredCaregiverUid?: string;
}

export interface IRescheduleConsultationAsAdminOrCaregiverLeadResponse {
    Success: boolean;
    Message?: string;
    TimeWasChanged: boolean;
    CaregiverWasChanged: boolean;
    NewCaregiverName?: string;
    RescheduledDateTime?: Date;
}

export interface IBaseOpenConsultationRequest {
    MobileNumber?: string;
    Subject: string;
    OnBehalfUID?: string;
    OnBehalfOfPetID?: string;
    ConsultationType: ConsultationTypeEnum;
    Category?: string | null;
    VerifyPayment?: IPaymentVerificationRequest;
    NotifyOwnDoctor?: boolean;
    QuestionnaireResult?: any;
    QuestionnaireSubmissionId?: string;
}

export interface IOpenDietitianAttachmentConsultationRequest extends Omit<
    IOpenMessageConsultationRequest,
    'ConsultationType' | 'Subject'
> {
    ConsultationType: ConsultationTypeEnum.DietitianUploadAttachmentMessage;
    Subject: 'Send files to the dietitian';
}

export interface IPaymentVerificationRequest {
    PackageName?: string;
    ProductID?: string;
    TransactionID: string;
}

export interface IOpenMessageConsultationRequest extends IBaseOpenConsultationRequest {
    MessageUid?: string;
    ConsultationUid?: string;
    Message: string;
    FileUids: string[];
    AiNurseInvolved?: boolean;
}

export interface IAddHouseholdMemberRequest {
    Name: string;
    Email: string;
    Cpr: string;
    ShouldCheckParentalAuthority?: boolean;
}

export interface ICompanyDetailsResponse {
    CreatedOn: Date;
    CompanyUid: string;
    CompanyName: string;
    SupportUrl: string;
    ReportingEmail: string;
    WhiteLists: ICompanyWhiteListDto[];
    IsInsuranceProvider: boolean;
}

export interface ICompanyDetails {
    CreatedOn: Date;
    CompanyUid: string;
    CompanyName: string;
    SupportUrl: string;
    ReportingEmail: string;
    WhiteLists: KnockoutObservableArray<ICompanyWhiteListDto>;
}

export interface ICompanyDto {
    CreatedOn: Date;
    UID: string;
    CompanyName: string;
    SupportURL: string;
    ReportingEmail: string;
    CompanyManagementGroupName: string;
    InsurancePolicyProvider?: InsurancePolicyProviderType;
}

export interface ICompanyWhiteListDto {
    CreatedOn: Date;
    CompanyWhiteListUid: string;
    Domain: string;
}

export interface IUpdateCompanyRequest {
    CompanyName: string;
    SupportUrl: string;
    ReportingEmail: string;
}

export interface ICreateCompanyWhiteListRequest {
    Domain: string;
}

export interface ICaregiverRole {
    key: CaregiverRoleEnum;
    name: string;
}

export interface IDoctorBaseDto {
    UserUid: string;
    UserId?: number | null;
    Email: string;
    FullName: string;
    PhoneNumber: string;
    CaregiverRole: CaregiverRoleEnum;
    IsConnectedToSmartplan: boolean;
    Description: string | null;
    PhotoUrl: string | null;
}

export interface ICaregiverTipsDto {
    Id?: number | null;
    Title: string;
    Text: string;
}

export interface IGetCaregiverTipsSingleResponse {
    CaregiverTip: ICaregiverTipsDto | null;
}

export interface IDoctorSignupRequest {
    FullName: string;
    Email: string;
    PhoneNumber: string;
    CaregiverRole: CaregiverRoleEnum;
    SendBookingNotifications: boolean;
    SendTextBookingNotificationsWhenOffDuty: boolean;
    Description: string;
    CustomSignature: string;
    Specializations: CaregiverSpecialization[];
}

export interface IDoctorSignupResponse {
    ResponseType: SignupDoctorResponseEnum;
    UserUid?: string;
}

export interface IUpdateSelfRequest {
    Name: string;
    Email: string;
    CPR: string;
    Mobile: string;
}

export interface IApproximateWaitingTimeResponse {
    IsOpen: boolean;
    IsQueueEnabled: boolean;
    ApproximateWaitingTime: string;
    IsLongWaitingTime: boolean;
    ConsultationStartTime: string | null;
    IsConsultationStartTimeSpecificallyRequested: boolean;
}

export interface IOpenVideoConsultationResult {
    Status: OpenVideoConsultationStatusEnum;
    NewConsultationGuid: string;
    RequiresPayment: boolean;
}

export interface IRescheduleVideoConsultationResult {
    Status: OpenVideoConsultationStatusEnum;
    ConsultationGuid: string;
}

export interface IOpenMessageConsultationResponse {
    ConsultationGuid: string;
    ConsultationMessageGuid: string;
    RequiresPayment: boolean;
    RequiresSubscription: boolean;
}

export interface IHangUpVideoCall {
    Session: string;
    PeerID: string;
}

export interface IServicePriceBaseResponse {
    Name: string;
    Price: number;
    ServiceType: ServiceTypeEnum;
}

export interface IUpdateServicePriceRequest {
    Price: number;
    ServiceType: ServiceTypeEnum;
}

export interface IWebPaymentPaymentRequest {
    ServiceType: ServiceTypeEnum;
}

export interface ICallForPatientResponse extends IVideoConferenceDtoResponse {
    ConsultationUid: string;
}

export interface IAdminConsultationResponse {
    Uid: string;
    ConsultationId: number;
    ConsultationMessage: number;
    IsReply: boolean;
    IsCancellable: boolean;
    CreatedOn: Date;
    HasResponseFromDoctor: boolean;
    ConsultationType: ConsultationTypeEnum;
    AvailableRelationType: AvailableRelationTypeEnum;
    DeviceType: DeviceTypeEnum;
    Rating: number;
    RatingComment: string;
    DoctorName: string;
    PaymentProvider: PaymentProvider;
    TransactionId: string;
    CompanyName: string;
    Patient: IAdminPatientDto;
}

export interface ICallQualityFeedbackResponse {
    Id: number;
    DoctorId: number;
    DoctorUid: string;
    DoctorName: string;
    DoctorUserAgent: string;
    PatientId: number;
    PatientUid: string;
    PatientName: string;
    PatientUserAgent: string;
    ConsultationId: number;
    ConsultationUid: string;
    DoctorVideoSessionUid: string;
    PatientVideoSessionUid: string;
    QualityOk: boolean;
    QualityBad: boolean;
    QualityBadSound: boolean;
    QualityBadVideo: boolean;
    QualityBadConnection: boolean;
    QualityNoConnection: boolean;
    QualityBadUserInterface: boolean;
    QualityBadOther: boolean;
    QualityDescription: string;
    CreatedOn: Date;
}

export interface IAdminPatientDto {
    Id: number;
    Uid: string;
    PatientName: string;
    PatientEmail: string;
    PartnerName: string;
    PartnerUid: string;
    CompanyName: string;
    CompanyUid: string;
    PhoneNumber: string;
    SignupDate: Date;
    LastLoginDate: Date;
    SubscriptionPlan: string;
    SubscriptionStatus: string;
    ReferralCode: string;
}

export interface IPagedResponse<T> {
    CurrentPage: number;
    PageCount: number;
    PageSize: number;
    RecordCount: number;
    Results: T[];
}

export interface IRatingResponse {
    Rating: number;
    RatingComment: string;
    DoctorName: string;
    ConsultationId: number;
}

// ReSharper restore InconsistentNaming

export interface IOpeningHourOverrideDto {
    ConsultationType: number;
    Date: string;
    PeriodsText: string;
}

export interface IInsurancePolicyProvider {
    InsurancePolicyProviderType: InsurancePolicyProviderType;
    Name: string;
    LogoPath: string;
    Description: string;
    PrefilledIdentifierDescription: string;
    CallToAction: string;
    ErrorMessage: string;
    IdentifierTerm: string;
    readonly ShowIdentifierField: boolean;
}

export interface IRedeemCouponRequest {
    CouponCode: string;
    PatientId: number;
}

export interface IUpdateCaregiverDataRequest {
    FullName: string;
    PhoneNumber: string;
    UserId: number;
    SendBookingNotifications: boolean;
    SendTextBookingNotificationsWhenOffDuty: boolean;
    Email: string;
    Description: string;
    CustomSignature: string;
    Specializations: CaregiverSpecialization[];
}

export interface ICallQualityFeedbackDto {
    ConsultationUid: string;
    VideoSessionUid: string;
    AzureCallId?: string;
}

export interface ICallQualityFeedbackRequest {
    QualityOk: boolean;
    QualityBad: boolean;
    QualityBadSound: boolean;
    QualityBadVideo: boolean;
    QualityBadConnection: boolean;
    QualityNoConnection: boolean;
    QualityBadUserInterface: boolean;
    QualityBadOther: boolean;
    QualityDescription: string;
    AzureCallId: string;
}

export interface IPatientUpcomingConsultationDto {
    UID: string;
    PatientId: number;
    HasUpcomingVideoConsultation: boolean;
}

export interface IVideoQualityDto {
    Date: Date;
    OkCount: number;
    BadCount: number;
    BadPercent: number;
    PatientRating: number;
}

export interface IPage<T> {
    Results: T[];
    CurrentPage: number;
    PageCount: number | null;
    PageSize: number;
    RecordCount: number | null;
    HasPreviousPage: boolean;
    HasNextPage: boolean;
}

export interface IReferralDto {
    Id: number;
    Type: number;
    DiagnosisCode: string;
    DiagnosisText: string;
    CreatedOn: Date;
    DoctorFullName: string;
    DoctorTitle: string;
    SenderOrganizationName: string;
    SenderStreetName: string;
    SenderPostalCode: string;
    SenderDistrictName: string;
    PatientFirstName: string;
    PatientLastName: string;
    PatientNationalIdentifier: string | null;
    ParentFirstName: string | null;
    ParentLastName: string | null;
    ParentIdentifier: string | null;
    PatientEmail: string | null;
    PatientPhoneNumber: string | null;
    PatientStreetName: string | null;
    PatientZipCode: string | null;
    PatientCityName: string | null;
    ReceiverLocationNumber: string;
    ReceiverName: string;
    ReceiverOrganizationName: string;
    ReceiverDepartmentName: string;
    ReceiverUnitName: string;
    ReceiverStreetName: string;
    ReceiverDistrictName: string;
    ReceiverPostalCode: string;
    DeliveryStatus: number;
    Attachments: IReferralAttachmentDto[];
}

export interface IReferralAttachmentDto {
    Reference: string;
    File: IFileDto;
    DeliveryStatus: number;
    DeliveredAt: string | null;
    FailedAt: string | null;
    FailureReason: string | null;
}

export interface IHospitalReferralDto extends IReferralDto {
    AnswerCCReceiverLocationNumber: string | null;
    AnswerCCReceiverName: string | null;
    AnswerCCReceiverOrganizationName: string | null;
    AnswerCCReceiverDepartmentName: string | null;
    AnswerCCReceiverUnitName: string | null;
    AnswerCCReceiverStreetName: string | null;
    AnswerCCReceiverDistrictName: string | null;
    AnswerCCReceiverPostalCode: string | null;
    CallPriority: number;
    Transport: number;
    Status: number;
    Anamnesis: string;
    ExaminationResults: string;
    DesiredExamination: string;
    Cave: string;
    Medicine: string;
    Other: string;
    Diagnoses: string;
    AdditionalDiagnoses: string[];
    AdditionalDiagnosisTexts: string[];
}

export interface IPsychologistReferralDto extends IReferralDto {
    Diagnosis: number;
    Reason: string;
    DeviationExplanation?: string | null;
}

export interface IPhysiotherapyReferralDto extends IReferralDto {
    Anamnesis: string;
    Medicine: string;
    Results: string;
    Cave: string;
    Other: string;
}

export interface ISpecialistReferralDto extends IReferralDto {
    ReferralCode: number;
    Anamnesis: string;
    Examination: string;
    Results: string;
    Cave: string;
    Medicine: string;
}

export interface ICaregiverShiftConsultationStatsDto {
    ShiftStart: string;
    ShiftEnd: string;
    CaregiverName: string;
    CaregiverId: number | null;
    Role: CaregiverRoleEnum;
    TextConsultationsCount: number;
    VideoConsultationsCount: number;
    TotalConsultationsCount: number;
    ConsultationsPerHour: number;
    ShiftHours: number;
    BookedHours: number;
    UniqueClientIds: string[];
}

export interface ICaregiverShiftStatsSummaryDto {
    TotalConsultations: number;
    TotalBookedHours: number;
    TotalShiftHours: number;
}

export interface IInternalTaskDto {
    Id: number;
    CreatedOn: string;
    Deadline?: string | null;
    ConsultationUid: string;
    TaskDescription: string;
    IsHandled: boolean;
    CompletedOn?: string | null;
    CreatedByCaregiverFullName: string;
    CreatedByCaregiverId: number;
    Patient?: IPatientSlimDto | null;
    Pet?: IPetSlimDto | null;
    ResponsibleCaregiverId: number;
    ResponsibleCaregiverFullName: string;
}

export interface SelectCaregiverDto {
    Uid: string;
    FullName: string;
    PhotoUrl: string;
    NextAvailableTime: string | null;
    Description: string | null;
}

export interface InsurancePolicyProviderDto {
    InsurancePolicyProviderType: InsurancePolicyProviderType;
    Name: string;
    LogoPath: string;
    LogoPathSmall: string;
    Description: string;
    ErrorMessage: string;
    IdentifierTerm: string;
    VideoOrMessageDisclaimer: string;
    AdultHouseholdMemberTerms: string;
    AdultHouseholdMemberWarning: string;
    ChildTerms: string;
    ChildTermsWarning: string;
    ChildTermsDescription: string;
    IsPublic: boolean;
    UseCprNumbers: boolean;
    HidePaidProducts: boolean;
    GrantsAccessTo: ServiceTypeEnum[];
    PreventPartnerAccessTo: ServiceTypeEnum[];
    HideBrandingOnSignupPages: boolean;
    PreventHouseholdMemberToManageChildren: boolean;
    SkipCategoryQuestion: boolean;
    CustomCaregiverNames: Record<CaregiverRoleEnum, string>;
}

export type FileEntity = {
    identifier: string;
    file: File;
    fileName: string;
    isUploaded: boolean;
    isPending: boolean;
    s3Key?: string;
    isFailed?: boolean;
    guid?: string;
    fileSizeBytes?: number;
};

export type PatientHouseholdMemberDto = {
    FullName: string;
    CprNumber: string;
    PatientId: number | null;
    DateOfBirth?: string;
    PhoneNumber?: string;
    Type: HouseholdMemberType;
};

export type CompanyAccessInfo = {
    Company?: ICompanyDto;
    InsurancePolicyProvider?: InsurancePolicyProviderDto;
    ChildTerms: string;
    HasAccess: boolean;
    IsAccessOwner: boolean;
    HasAccessThroughHouseholdMember: boolean;
    AccessProvidingHouseholdMemberName?: string;
    AccessProvidingHouseholdMemberID?: number;
    HasAccessThroughInsuranceProvider: boolean;
    HasAccessThroughEmailWhitelist: boolean;
    HasAccessThroughReepaySubscription: boolean;
    PolicyNumber?: string;
    ShouldShowPolicyNumber: boolean;
    AdultHouseholdMemberTerms?: string;
    HasAdolescentAccess: boolean;
    AdolescentAccessOwnerName?: string;
};

export interface ProductDto {
    ConsultationType: ConsultationTypeEnum;
    AvailableCategories: ConsultationCategoriesDto;
    ReplyServiceType?: ServiceTypeEnum | null;
    HandledBy: CaregiverRoleEnum;
    Price: ServicePriceBaseResponse;
    IsQueueEnabled: boolean;
    CanQueueNow: boolean;
    IsVideo: boolean;
    IsActive: boolean;
    IsHidden: boolean;
    Texts: ProductTextsDto;
    CanReply: boolean;
    IsOnline?: boolean | null;
    IsAttachmentsEnabled: boolean;
    IsAttachmentsRequired: boolean;
    ChildrenSelectionEnabled: boolean;
    SkipSubjectForm: boolean;
    CaregiverSelectionStatus: CaregiverSelectionStatus;
    RequiredQuestionnaire?: string;
    NeverShowMissingCoverageWarning: boolean;
    RequiresRecentConsultationsCheck: boolean;
}

interface ConsultationCategoryDto {
    Id: string;
    InternalText: string;
    Text: string;
}

interface ConsultationCategoriesDto {
    SelectCategoryTitle: string;
    CategoryRequiredMessage: string | null;
    Categories: ConsultationCategoryDto[];
}

interface ProductTextsDto {
    CaregiverName: string;
    CaregiverNameIndefinite: string;
    CaregiverNamePlural: string;
    ProductName: string;
    Disclaimer: string;
    CallToAction: string;
    QueueEnabled: string;
    Profession: string;
    AcheDescription: string;
    Action: string;
    Tag: string;
    CancelWithoutRefundWarning: string;
    ImportantInfo: string;
}

export enum CaregiverSelectionStatus {
    Disabled = 0,
    AllowSelection = 1,
    ForceSelection = 2,
}

interface ServicePriceBaseResponse {
    Name: string;
    DefaultPrice: number;
    Price: number;
    ServiceType: ServiceTypeEnum;
    Currency: string;
    PriceOverride?: number | null;
    ShouldAddVat: boolean;
}
